import { useEffect, useState } from "react";

type Setter<T> = (t: T) => T;

type SetValueFn<T> = (value: T | Setter<T>) => void;

export default function useLocalStorage<T>(
  key: string | null | undefined,
  initialValue: T
): [T, SetValueFn<T>] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      // Get from local storage by key
      if (!key) {
        return initialValue;
      }
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      // If error also return initialValue
      return initialValue;
    }
  });

  useEffect(() => {
    if (!key) {
      return;
    }
    try {
      const item = window.localStorage.getItem(key);
      setStoredValue(item ? (JSON.parse(item) as T) : initialValue);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
    // We only want to run this when key changes - adding setStoredValue and initialValue causes some weird side effects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage.
  const setValue = (value: T | Setter<T>) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      if (typeof window !== "undefined" && key) {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
  return [storedValue, setValue];
}
